/**
 * MarthaEditor — Unified document editor for Martha V5.
 * Replaces BlockNoteEditor, RequirementsEditorV2, and DocumentEditor
 * with a single composable component. Feature modules enabled via props.
 *
 * Features:
 * - Custom block schema (MarthaQuestion, DocumentLink, MermaidDiagram)
 * - Large document performance (chunked loading, content-visibility CSS, Web Worker)
 * - Block-based heading outline with click-to-scroll (toggleable Cmd+Shift+O)
 * - Custom slash commands (/mermaid, /divider, /callout)
 * - Footer stats (words, blocks, reading time)
 * - Current section breadcrumb
 * - Mermaid diagrams as first-class blocks (no MutationObserver)
 * - Multi-user collaboration via Yjs (when enabled)
 * - Martha AI orchestration (when enabled)
 * - Inline comments (when enabled)
 * - Keyboard shortcuts (Cmd+Shift+M: mermaid, Cmd+Shift+O: outline)
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  lazy,
  Suspense,
} from 'react';
import { BlockNoteEditor as BlockNoteEditorCore, BlockNoteSchema, defaultBlockSpecs, defaultInlineContentSpecs } from '@blocknote/core';
import {
  useCreateBlockNote,
  FormattingToolbarController,
  FormattingToolbar,
  BlockTypeSelect,
  BasicTextStyleButton,
  TextAlignButton,
  ColorStyleButton,
  NestBlockButton,
  CreateLinkButton,
  SuggestionMenuController,
  SideMenuController,
  SideMenu,
  DragHandleButton,
  DragHandleMenu,
  AddBlockButton,
  RemoveBlockItem,
  BlockColorsItem,
  getDefaultReactSlashMenuItems,
  useComponentsContext,
  useBlockNoteEditor,
} from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
// Note: @blocknote/core/fonts/inter.css intentionally NOT imported — we use Source Serif 4 for doc content
import '@blocknote/mantine/style.css';

import { MarthaEditorProvider, type MarthaEditorFeatures } from './MarthaEditorContext';
import { parseContent } from './utils/content-parser';
import { blocksToJSON, blocksToMarkdown } from './utils/blocks-to-json';
import { editorThemeCSS } from './styles/editor-theme';
import { getCustomBlockSchema } from '../requirements-editor-v2/custom-blocks';
import { mermaidBlockSpec } from './custom-blocks/MermaidBlock';
import { calloutBlockSpec } from './custom-blocks/CalloutBlock';
import { embedBlockSpec } from './custom-blocks/EmbedBlock';
import { toggleBlockSpec } from './custom-blocks/ToggleBlock';
import { columnBlockSpec } from './custom-blocks/ColumnBlock';
import { syncedBlockSpec } from './custom-blocks/SyncedBlock';
import { mentionInlineSpec, type MentionSuggestionItem } from './custom-blocks/MentionInline';
import { EditorOutline } from './features/EditorOutline';
import { EditorFooter } from './features/EditorFooter';
import { EditorBreadcrumb } from './features/EditorBreadcrumb';
import { OfflineBanner } from './features/CollaborationFeature';

// Lazy-loaded features (dynamic import to avoid Rollup TDZ)
const CommentsPanel = lazy(() => import('./features/CommentsFeature'));
const AiFeature = lazy(() => import('./features/AiFeature'));

// BlockNote v0.46+ uses complex generic types for blocks
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BlockType = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PartialBlockType = any;

// ─── Debounce utility ──────────────────────────────────────────────────────────

function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

// ─── Slash menu utilities (Phase 3 & 6) ──────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filterSlashMenuItems(items: any[], query: string): any[] {
  if (!query) return items;
  const q = query.toLowerCase();
  return items.filter(
    (item) =>
      item.title?.toLowerCase().includes(q) ||
      item.aliases?.some((a: string) => a.toLowerCase().includes(q))
  );
}

// Module-level SVG icons — stable references prevent re-render loops in slash menu
const MermaidIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="18" r="3" />
    <circle cx="6" cy="6" r="3" />
    <path d="M13 6h3a2 2 0 0 1 2 2v7" />
    <path d="M6 9v12" />
  </svg>
);

const DividerIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="2" y1="12" x2="22" y2="12" />
  </svg>
);

const CalloutIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const EmbedIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

const ToggleIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const ColumnsIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="7" height="18" rx="1" />
    <rect x="14" y="3" width="7" height="18" rx="1" />
  </svg>
);

const SyncedIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// Insert or update block — replaces empty paragraph, otherwise inserts after
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function insertCustomBlock(editor: any, blockConfig: any) {
  const currentBlock = editor.getTextCursorPosition().block;
  const isEmpty =
    currentBlock.type === 'paragraph' &&
    (!currentBlock.content ||
      currentBlock.content.length === 0 ||
      (currentBlock.content.length === 1 &&
        currentBlock.content[0].type === 'text' &&
        !currentBlock.content[0].text));
  if (isEmpty) {
    editor.updateBlock(currentBlock, blockConfig);
  } else {
    editor.insertBlocks([blockConfig], currentBlock, 'after');
  }
}

// ─── Ref API ────────────────────────────────────────────────────────────────────

export interface MarthaEditorRef {
  getCurrentBlocks: () => BlockType[];
  getMarkdown: () => Promise<string>;
  getHTML: () => Promise<string>;
  focus: () => void;
  insertBlocks: (blocks: PartialBlockType[]) => void;
  getEditor: () => BlockNoteEditorCore | null;
  toggleOutline: () => void;
}

// ─── Collaboration config (Phase 4) ─────────────────────────────────────────────

export interface CollaborationConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fragment: any; // Y.XmlFragment
  user: { name: string; color: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  provider?: { awareness: any };
}

// ─── Props ──────────────────────────────────────────────────────────────────────

export interface MarthaEditorProps {
  // Core
  documentId?: string;
  projectId?: string;
  initialContent?: string;
  contentFormat?: string;
  readOnly?: boolean;

  // Features (all default false except mermaid/stats)
  features?: Partial<MarthaEditorFeatures>;

  // Callbacks
  onChange?: (content: string, contentFormat: 'blocknote') => void;
  onEditorReady?: (editor: BlockNoteEditorCore) => void;
  onSelectionChange?: (text: string, start: number, end: number) => void;
  onCommentClick?: () => void;

  // Collaboration (externally provided Yjs config)
  userId?: string;
  userName?: string;
  collaborationConfig?: CollaborationConfig;
  isCollaborationConnected?: boolean;
  /** True once Yjs sync step 2 completes — triggers immediate fallback if doc is empty */
  isCollaborationSynced?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  collaborationUsers?: Array<{ id: string; name: string; color?: string }>;

  // AI adapter (workspace mode — passes through to AiFeature)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  aiApi?: any;

  // Layout
  className?: string;
  placeholder?: string;
  autoSaveDelay?: number;
}

// ─── Comment toolbar button ─────────────────────────────────────────────────────

function CommentToolbarButton({ onClick }: { onClick?: (selectedText: string, blockId: string) => void }) {
  const Components = useComponentsContext();
  const editor = useBlockNoteEditor();

  if (!Components || !onClick) return null;

  const handleClick = () => {
    // Capture selected text NOW, before the click blurs the selection
    let selectedText = '';
    let blockId = '';

    // Try BlockNote selection first
    const selection = editor.getSelection();
    if (selection && selection.blocks.length > 0) {
      const block = selection.blocks[0];
      blockId = block.id;
      if (Array.isArray(block.content)) {
        selectedText = block.content
          .map((item: { type: string; text?: string }) => item.type === 'text' ? item.text : '')
          .join('');
      }
    }

    // Fallback to native browser selection
    if (!selectedText) {
      const nativeSel = window.getSelection();
      if (nativeSel && nativeSel.toString().trim()) {
        selectedText = nativeSel.toString().trim();
      }
    }

    // Fallback to current cursor block
    if (!selectedText) {
      try {
        const cursorPos = editor.getTextCursorPosition();
        if (cursorPos?.block) {
          blockId = cursorPos.block.id;
          if (Array.isArray(cursorPos.block.content)) {
            selectedText = cursorPos.block.content
              .map((item: { type: string; text?: string }) => item.type === 'text' ? item.text : '')
              .join('');
          }
        }
      } catch { /* ignore */ }
    }

    if (selectedText) {
      onClick(selectedText, blockId);
    }
  };

  return (
    <Components.FormattingToolbar.Button
      mainTooltip="Add Comment (select text first)"
      onClick={handleClick}
      icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      }
    />
  );
}

// ─── Copy block menu item (uses BlockNote's internal Components for consistent styling) ──

function CopyBlockItem({ block, children }: { block: any; children: React.ReactNode }) {
  const Components = useComponentsContext();
  const editor = useBlockNoteEditor();

  if (!Components) return null;

  return (
    <Components.Generic.Menu.Item
      className="bn-menu-item"
      onClick={() => {
        if (!block || !editor) return;

        // Extract plain text from the block's inline content (recursive for links)
        const extractText = (content: any): string => {
          if (!Array.isArray(content)) return '';
          return content
            .map((c: any) => {
              if (c.type === 'text') return c.text || '';
              if (c.type === 'link' && Array.isArray(c.content)) return extractText(c.content);
              return '';
            })
            .join('');
        };

        // Also extract from nested children blocks (list items)
        const extractAll = (b: any): string => {
          let text = extractText(b.content || []);
          if (Array.isArray(b.children)) {
            for (const child of b.children) {
              const childText = extractAll(child);
              if (childText) text += '\n' + childText;
            }
          }
          return text;
        };

        const text = extractAll(block);
        if (!text) return;

        // Synchronous copy via textarea + execCommand (works reliably in menu dropdowns)
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        try {
          document.execCommand('copy');
        } finally {
          document.body.removeChild(ta);
        }
      }}
    >
      {children}
    </Components.Generic.Menu.Item>
  );
}

// ─── Custom schema (built once) ─────────────────────────────────────────────────

const schema = BlockNoteSchema.create({
  blockSpecs: {
    ...defaultBlockSpecs,
    ...getCustomBlockSchema(),
    // createReactBlockSpec returns a factory — call with () to get BlockSpec
    mermaidDiagram: mermaidBlockSpec(),
    callout: calloutBlockSpec(),
    embed: embedBlockSpec(),
    toggle: toggleBlockSpec(),
    columns: columnBlockSpec(),
    syncedBlock: syncedBlockSpec(),
  },
  inlineContentSpecs: {
    ...defaultInlineContentSpecs,
    // @-mention inline chip (page or user)
    mention: mentionInlineSpec,
  },
});

// ─── Main component ─────────────────────────────────────────────────────────────

export const MarthaEditor = forwardRef<MarthaEditorRef, MarthaEditorProps>(
  function MarthaEditor(
    {
      documentId,
      projectId,
      initialContent = '',
      contentFormat,
      readOnly = false,
      features: featuresProp,
      onChange,
      onEditorReady,
      onSelectionChange,
      onCommentClick,
      userId,
      userName,
      collaborationConfig,
      isCollaborationConnected,
      isCollaborationSynced,
      collaborationUsers,
      className = '',
      placeholder = 'Start typing...',
      autoSaveDelay = 1000,
      aiApi,
    },
    ref
  ) {
    // Resolve feature flags with defaults
    const features: MarthaEditorFeatures = useMemo(
      () => ({
        collaboration: false,
        outline: false,
        comments: false,
        ai: false,
        mermaid: true,
        stats: true,
        breadcrumb: false,
        ...featuresProp,
      }),
      [featuresProp]
    );

    const hasInitialized = useRef(false);
    const lastContent = useRef(initialContent);
    const [loadProgress, setLoadProgress] = useState<
      { loaded: number; total: number } | undefined
    >();
    const [isLoading, setIsLoading] = useState(
      !!initialContent && !collaborationConfig
    );
    const [initError, setInitError] = useState<string | null>(null);
    const [showOutline, setShowOutline] = useState(features.outline);
    const [showComments, setShowComments] = useState(false);
    const [commentsKey, setCommentsKey] = useState(0);

    // Phase 4: Create the editor with optional collaboration config
    const editor = useCreateBlockNote({
      schema,
      ...(collaborationConfig ? { collaboration: collaborationConfig } : {}),
      domAttributes: {
        editor: { class: 'martha-editor-inner' },
      },
    });

    // ─── Content initialization ───────────────────────────────────────────────
    // Skip when collaboration is active (Yjs is the source of truth)

    // ─── Collaboration fallback helper ────────────────────────────────────────
    // Shared flag: prevents the isSynced effect and the emergency timer from
    // both calling replaceBlocks if they race.
    const collabFallbackFired = useRef(false);

    const runCollabFallback = useCallback(async (reason: string) => {
      if (collabFallbackFired.current || !editor || !initialContent) return;
      const docBlocks = editor.document;
      const hasContent =
        docBlocks.length > 1 ||
        (docBlocks.length === 1 &&
          docBlocks[0].content &&
          Array.isArray(docBlocks[0].content) &&
          docBlocks[0].content.length > 0);
      if (hasContent) return; // Yjs has content — nothing to do
      collabFallbackFired.current = true;
      console.warn(`[MarthaEditor] Collab doc empty (${reason}) — loading initialContent directly`);
      try {
        const blocks = await parseContent(
          editor as unknown as BlockNoteEditorCore,
          initialContent,
          contentFormat,
        );
        if (blocks.length > 0) {
          // Defer past any in-progress React 18 concurrent render chunks to
          // avoid error #301 ("Cannot update a component while rendering a
          // different component") when replaceBlocks synchronously dispatches
          // a ProseMirror transaction from inside our collab-fallback effect.
          await new Promise<void>((resolve) => setTimeout(resolve, 0));
          try {
            editor.replaceBlocks(editor.document, blocks);
            lastContent.current = blocksToJSON(editor as unknown as BlockNoteEditorCore);
          } catch (renderErr) {
            console.error('[MarthaEditor] replaceBlocks failed — paragraph fallback:', renderErr);
            editor.replaceBlocks(editor.document, [{
              type: 'paragraph' as const,
              content: [{ type: 'text' as const, text: initialContent.slice(0, 500) + (initialContent.length > 500 ? '…' : '') }],
            }]);
          }
        }
      } catch (err) {
        console.error('[MarthaEditor] Fallback content load failed:', err);
      }
    }, [editor, initialContent, contentFormat]);

    useEffect(() => {
      if (collaborationConfig) {
        // Collaboration mode — Yjs is the source of truth.
        // runCollabFallback is triggered reactively (see isSynced effect below)
        // plus a 5s emergency timer if isSynced never fires.
        hasInitialized.current = true;
        collabFallbackFired.current = false; // reset on (re)mount
        setIsLoading(false);
        onEditorReady?.(editor as unknown as BlockNoteEditorCore);
        return;
      }

      if (hasInitialized.current || !editor || !initialContent) {
        if (!initialContent) setIsLoading(false);
        return;
      }

      let cancelled = false;

      const initContent = async () => {
        try {
          const blocks = await parseContent(
            editor as unknown as BlockNoteEditorCore,
            initialContent,
            contentFormat
          );
          if (cancelled || blocks.length === 0) {
            hasInitialized.current = true;
            setIsLoading(false);
            onEditorReady?.(editor as unknown as BlockNoteEditorCore);
            return;
          }

          // Phase 2: Tuned chunked insertion
          const CHUNK_THRESHOLD = 200;
          if (blocks.length <= CHUNK_THRESHOLD) {
            editor.replaceBlocks(editor.document, blocks);
            hasInitialized.current = true;
            // Snapshot the serialized JSON so onChange comparison works correctly
            // even when initialContent was markdown (avoids markdown≠JSON mismatch loop)
            lastContent.current = blocksToJSON(editor as unknown as BlockNoteEditorCore);
            setIsLoading(false);
            onEditorReady?.(editor as unknown as BlockNoteEditorCore);
            return;
          }

          // Large documents: smaller initial batch, larger chunks
          const INITIAL_BATCH = 40;
          const CHUNK_SIZE = 100;

          console.log(
            `[MarthaEditor] Chunked insert: ${blocks.length} blocks (initial=${INITIAL_BATCH}, chunk=${CHUNK_SIZE})`
          );

          editor.replaceBlocks(editor.document, blocks.slice(0, INITIAL_BATCH));
          setLoadProgress({ loaded: INITIAL_BATCH, total: blocks.length });

          let offset = INITIAL_BATCH;

          const insertNextChunk = () => {
            if (cancelled || offset >= blocks.length) {
              if (!cancelled) {
                hasInitialized.current = true;
                lastContent.current = blocksToJSON(editor as unknown as BlockNoteEditorCore);
                setIsLoading(false);
                setLoadProgress(undefined);
                onEditorReady?.(editor as unknown as BlockNoteEditorCore);
                console.log(
                  `[MarthaEditor] Chunked insert complete: ${blocks.length} blocks`
                );
              }
              return;
            }

            const chunk = blocks.slice(offset, offset + CHUNK_SIZE);
            try {
              const currentDoc = editor.document;
              const lastBlock = currentDoc[currentDoc.length - 1];
              if (lastBlock) {
                editor.insertBlocks(chunk, lastBlock, 'after');
              }
            } catch (err) {
              console.warn(
                '[MarthaEditor] Chunk insert error at offset',
                offset,
                err
              );
            }

            offset += CHUNK_SIZE;
            setLoadProgress({
              loaded: Math.min(offset, blocks.length),
              total: blocks.length,
            });

            if (typeof requestIdleCallback !== 'undefined') {
              requestIdleCallback(() => insertNextChunk(), { timeout: 50 });
            } else {
              setTimeout(insertNextChunk, 0);
            }
          };

          setTimeout(insertNextChunk, 16);
        } catch (error) {
          console.error('Failed to initialize editor content:', error);
          const msg = error instanceof Error ? error.message : String(error);
          setInitError(`Content load failed: ${msg}`);
          hasInitialized.current = true;
          setIsLoading(false);
          onEditorReady?.(editor as unknown as BlockNoteEditorCore);
        }
      };

      initContent();

      return () => {
        cancelled = true;
      };
    }, [editor, initialContent, contentFormat, onEditorReady, collaborationConfig]);

    // ─── Collaboration fallback: reactive on isSynced ─────────────────────────
    // When Yjs sync completes (step 2), check immediately if the doc is empty.
    // If so, load initialContent as fallback (new doc, never had Yjs content).
    // This replaces the old fixed 3-second timer for the happy path.
    useEffect(() => {
      if (!collaborationConfig || !isCollaborationSynced) return;
      void runCollabFallback('isSynced');
    }, [collaborationConfig, isCollaborationSynced, runCollabFallback]);

    // ─── Collaboration fallback: 5s emergency timer ───────────────────────────
    // Safety net for cases where isSynced never fires (e.g. server unreachable,
    // provider not passed, or running without the isSynced prop wired up).
    useEffect(() => {
      if (!collaborationConfig || !initialContent) return;
      const timer = setTimeout(() => {
        void runCollabFallback('5s-emergency');
      }, 5000);
      return () => clearTimeout(timer);
    }, [collaborationConfig, initialContent, runCollabFallback]);

    // ─── Debounced onChange — emits BlockNote JSON ────────────────────────────
    // Always active — collaboration is for real-time presence only; document-service is persistence

    const debouncedOnChange = useMemo(() => {
      if (!onChange) return null;
      return debounce(() => {
        if (!editor) return;
        const json = blocksToJSON(editor as unknown as BlockNoteEditorCore);
        if (json !== lastContent.current) {
          lastContent.current = json;
          onChange(json, 'blocknote');
        }
      }, autoSaveDelay);
    }, [editor, onChange, autoSaveDelay]);

    const handleChange = useCallback(() => {
      // Suppress onChange during initialization — chunked block insertion fires
      // BlockNote onChange on every chunk, and the serialized JSON will always
      // differ from the original markdown in lastContent.current, causing the
      // parent to re-render with new initialContent → infinite update loop.
      if (!hasInitialized.current) return;
      debouncedOnChange?.();
    }, [debouncedOnChange]);

    // ─── Selection handling ──────────────────────────────────────────────────

    const handleSelectionChange = useCallback(() => {
      if (!onSelectionChange || !editor) return;

      const selection = editor.getSelection();
      if (selection) {
        const selectedBlocks = selection.blocks;
        if (selectedBlocks.length > 0) {
          const firstBlock = selectedBlocks[0];
          if (firstBlock.content && Array.isArray(firstBlock.content)) {
            const text = firstBlock.content
              .map((item: { type: string; text?: string }) =>
                item.type === 'text' ? item.text : ''
              )
              .join('');
            if (text) {
              onSelectionChange(text, 0, text.length);
            }
          }
        }
      }
    }, [editor, onSelectionChange]);

    // ─── C1a: Preload mermaid chunk when content has mermaid blocks ─────────

    useEffect(() => {
      if (!features.mermaid || !editor) return;
      try {
        const hasMermaid = editor.document.some(
          (b: { type: string }) => b.type === 'mermaidDiagram'
        );
        if (hasMermaid) {
          import('../documentation/MermaidDiagram'); // preload chunk
        }
      } catch { /* ignore during init */ }
    }, [editor, features.mermaid, isLoading]);

    // ─── Phase 3 & 6: Custom slash menu items (memoized with stable icons) ──

    const customSlashMenuItems = useMemo(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const items: any[] = [];

      // /mermaid — insert mermaid diagram block
      if (features.mermaid) {
        items.push({
          title: 'Mermaid Diagram',
          onItemClick: () =>
            insertCustomBlock(editor, {
              type: 'mermaidDiagram',
              props: { code: 'graph LR\n  A --> B' },
            }),
          aliases: ['mermaid', 'diagram', 'chart', 'flowchart', 'sequence'],
          group: 'Media',
          icon: MermaidIcon,
          subtext: 'Flowchart, sequence diagram, etc.',
        });
      }

      // /divider — insert horizontal rule
      items.push({
        title: 'Divider',
        onItemClick: () =>
          insertCustomBlock(editor, {
            type: 'paragraph',
            content: [{ type: 'text', text: '---' }],
          }),
        aliases: ['divider', 'hr', 'separator', 'line'],
        group: 'Basic blocks',
        icon: DividerIcon,
        subtext: 'Horizontal divider line',
      });

      // /callout — insert callout block
      items.push({
        title: 'Callout',
        onItemClick: () =>
          insertCustomBlock(editor, {
            type: 'callout',
            props: { variant: 'info' },
          }),
        aliases: ['callout', 'alert', 'note', 'warning', 'info', 'tip'],
        group: 'Basic blocks',
        icon: CalloutIcon,
        subtext: 'Highlighted callout block',
      });

      // /embed — insert embed block
      items.push({
        title: 'Embed',
        onItemClick: () =>
          insertCustomBlock(editor, {
            type: 'embed',
            props: { url: '' },
          }),
        aliases: ['embed', 'iframe', 'youtube', 'figma', 'loom', 'video'],
        group: 'Media',
        icon: EmbedIcon,
        subtext: 'YouTube, Figma, Loom, or any URL',
      });

      // /toggle — insert toggle block
      items.push({
        title: 'Toggle',
        onItemClick: () =>
          insertCustomBlock(editor, {
            type: 'toggle',
            props: { isOpen: 'true', childContent: '' },
          }),
        aliases: ['toggle', 'collapse', 'expandable', 'accordion', 'dropdown'],
        group: 'Basic blocks',
        icon: ToggleIcon,
        subtext: 'Collapsible content section',
      });

      // /columns — insert 2-column layout
      items.push({
        title: '2 Columns',
        onItemClick: () =>
          insertCustomBlock(editor, {
            type: 'columns',
            props: { columnCount: '2', columns: '["",""]' },
          }),
        aliases: ['columns', '2col', 'split', 'side-by-side', 'layout'],
        group: 'Layout',
        icon: ColumnsIcon,
        subtext: 'Side-by-side 2-column layout',
      });

      // /3columns — insert 3-column layout
      items.push({
        title: '3 Columns',
        onItemClick: () =>
          insertCustomBlock(editor, {
            type: 'columns',
            props: { columnCount: '3', columns: '["","",""]' },
          }),
        aliases: ['3columns', '3col', 'triple'],
        group: 'Layout',
        icon: ColumnsIcon,
        subtext: 'Three-column layout',
      });

      // /synced — insert synced block
      items.push({
        title: 'Synced Block',
        onItemClick: () =>
          insertCustomBlock(editor, {
            type: 'syncedBlock',
            props: { syncId: '' },
          }),
        aliases: ['synced', 'shared', 'linked', 'reusable'],
        group: 'Advanced',
        icon: SyncedIcon,
        subtext: 'Content shared across pages',
      });

      return items;
    }, [editor, features.mermaid]);

    // Memoized getItems for SuggestionMenuController — prevents infinite re-render
    const getSlashMenuItems = useCallback(
      async (query: string) =>
        filterSlashMenuItems(
          [...getDefaultReactSlashMenuItems(editor), ...customSlashMenuItems],
          query
        ),
      [editor, customSlashMenuItems]
    );

    // @-mention suggestion items — search pages and users
    const getMentionItems = useCallback(
      async (query: string): Promise<MentionSuggestionItem[]> => {
        if (!query || query.length < 1) return [];
        const items: MentionSuggestionItem[] = [];
        try {
          // Search pages
          const { pagesApi } = await import('../../api/pages');
          const { pages } = await pagesApi.search(query, 6);
          for (const page of pages) {
            items.push({
              id: page.id,
              name: page.title || 'Untitled',
              kind: 'page',
              icon: page.icon,
              subtitle: page.area,
            });
          }
        } catch { /* ignore search errors */ }
        try {
          // Search workspace members
          const { authApi } = await import('../../api/auth');
          const wsSlug = window.location.pathname.split('/')[2] || '';
          if (wsSlug) {
            const { data: members } = await authApi.listMembers(wsSlug, 6);
            for (const m of members) {
              const name = m.user.name || m.user.email;
              if (name.toLowerCase().includes(query.toLowerCase())) {
                items.push({
                  id: m.userId,
                  name,
                  kind: 'user',
                  subtitle: m.user.email,
                });
              }
            }
          }
        } catch { /* ignore auth errors */ }
        return items;
      },
      []
    );

    // Convert mention items to SuggestionMenuController format
    const getMentionMenuItems = useCallback(
      async (query: string) => {
        const suggestions = await getMentionItems(query);
        return suggestions.map((item) => ({
          title: item.name,
          onItemClick: () => {
            (editor as any).insertInlineContent([
              {
                type: 'mention',
                props: { id: item.id, name: item.name, kind: item.kind },
              },
              ' ',
            ]);
          },
          aliases: item.subtitle ? [item.subtitle] : [],
          group: item.kind === 'page' ? 'Pages' : 'People',
          icon: item.kind === 'page' ? (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          ),
          subtext: item.subtitle || '',
        }));
      },
      [editor, getMentionItems]
    );

    // ─── Phase 5 & 6: Keyboard shortcuts ─────────────────────────────────────

    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        const isMod = e.metaKey || e.ctrlKey;

        // Cmd+Shift+O — toggle outline sidebar
        if (isMod && e.shiftKey && e.key.toLowerCase() === 'o') {
          if (features.outline) {
            e.preventDefault();
            setShowOutline((prev) => !prev);
          }
        }

        // Cmd+Shift+M — insert mermaid block (not Cmd+Alt+M which is comments)
        if (
          isMod &&
          e.shiftKey &&
          !e.altKey &&
          e.key.toLowerCase() === 'm'
        ) {
          if (features.mermaid && editor && !readOnly) {
            e.preventDefault();
            insertCustomBlock(editor, {
              type: 'mermaidDiagram',
              props: { code: 'graph LR\n  A --> B' },
            });
          }
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [editor, features.outline, features.mermaid, readOnly]);

    // ─── Comment creation (inline popover, no window.prompt) ───────────────

    const [commentPopover, setCommentPopover] = useState<{
      selectedText: string;
      blockId: string;
    } | null>(null);
    const [commentText, setCommentText] = useState('');
    const commentInputRef = useRef<HTMLInputElement>(null);

    const handleCommentClick = useCallback((selectedText: string, blockId: string) => {
      if (!projectId || !documentId) return;
      if (!selectedText) return;
      setCommentPopover({ selectedText, blockId });
      setCommentText('');
      // Focus input after render
      setTimeout(() => commentInputRef.current?.focus(), 50);
    }, [projectId, documentId]);

    const submitComment = useCallback(() => {
      if (!commentPopover || !commentText.trim() || !projectId || !documentId) return;
      const { selectedText, blockId } = commentPopover;

      fetch(`/api/projects/${projectId}/editor/${documentId}/martha-comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          anchor_type: 'selection',
          anchor_text: selectedText,
          anchor_section_id: blockId,
          user_comment: commentText,
          user_name: userName || 'User',
          user_id: userId,
        }),
      }).then(() => {
        setCommentsKey(k => k + 1);
        setShowComments(true);
        setCommentPopover(null);
        setCommentText('');
      }).catch(err => console.error('[MarthaEditor] Comment failed:', err));
    }, [commentPopover, commentText, projectId, documentId, userName, userId]);

    // ─── Ref API ─────────────────────────────────────────────────────────────

    useImperativeHandle(
      ref,
      () => ({
        getCurrentBlocks: () => editor?.document ?? [],
        getMarkdown: async () => {
          if (!editor) return '';
          return blocksToMarkdown(editor as unknown as BlockNoteEditorCore);
        },
        getHTML: async () => {
          if (!editor) return '';
          try {
            return await (
              editor as unknown as BlockNoteEditorCore
            ).blocksToHTMLLossy(editor.document);
          } catch {
            return '';
          }
        },
        focus: () => editor?.focus(),
        insertBlocks: (blocks: PartialBlockType[]) => {
          if (!editor) return;
          const currentBlock = editor.getTextCursorPosition().block;
          editor.insertBlocks(blocks, currentBlock, 'after');
        },
        getEditor: () => editor as unknown as BlockNoteEditorCore | null,
        toggleOutline: () => setShowOutline((prev) => !prev),
      }),
      [editor]
    );

    // ─── Context value ───────────────────────────────────────────────────────

    const ctxValue = useMemo(
      () => ({
        editor: editor as unknown as BlockNoteEditorCore | null,
        features,
        readOnly,
        documentId,
        projectId,
        isLoading,
        loadProgress,
      }),
      [editor, features, readOnly, documentId, projectId, isLoading, loadProgress]
    );

    // ─── Render ──────────────────────────────────────────────────────────────

    const isCollabActive = features.collaboration && !!collaborationConfig;

    return (
      <MarthaEditorProvider value={ctxValue}>
        <div className={`martha-editor-root flex flex-col h-full ${className}`}>
          {/* Phase 4: Collaboration header — presence avatars */}
          {isCollabActive &&
            collaborationUsers &&
            collaborationUsers.length > 0 && (
              <div className="flex items-center gap-2 px-4 py-1.5 border-b border-stone-100 bg-stone-50 text-xs">
                <div className="flex -space-x-2">
                  {collaborationUsers.slice(0, 5).map((user) => (
                    <div
                      key={user.id}
                      className="w-6 h-6 rounded-full border-2 border-white flex items-center justify-center text-[10px] font-medium text-white"
                      style={{ backgroundColor: user.color || '#f97316' }}
                      title={user.name}
                    >
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                  ))}
                </div>
                <span className="text-stone-500">
                  {collaborationUsers.length} user
                  {collaborationUsers.length !== 1 ? 's' : ''} editing
                </span>
                <div
                  className={`w-2 h-2 rounded-full ${isCollaborationConnected ? 'bg-green-400' : 'bg-amber-400'}`}
                />
              </div>
            )}

          {/* Phase 4: Offline banner — show when collaboration enabled but disconnected */}
          {features.collaboration && isCollaborationConnected === false && (
            <OfflineBanner isConnected={false} enabled={true} />
          )}

          {/* Phase 5: Breadcrumb */}
          {features.breadcrumb && (
            <EditorBreadcrumb
              editor={editor as unknown as BlockNoteEditorCore}
            />
          )}

          {/* Main editor area */}
          <div className="flex-1 flex relative overflow-hidden">
            {/* Editor container */}
            <div
              className="martha-editor-container flex-1 h-full overflow-auto"
              onClick={() => editor?.focus()}
              style={{
                backgroundColor: '#ffffff',
                color: '#1c1917',
                colorScheme: 'light',
              }}
            >
              {/* Loading progress indicator */}
              {isLoading && loadProgress && (
                <div className="sticky top-0 z-10 px-4 py-1 bg-stone-50 border-b border-stone-100 text-xs text-stone-500">
                  Loading... {loadProgress.loaded}/{loadProgress.total} blocks
                  <div className="h-0.5 bg-stone-200 rounded-full mt-1">
                    <div
                      className="h-full bg-coral-400 rounded-full transition-all duration-300"
                      style={{
                        width: `${(loadProgress.loaded / loadProgress.total) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              )}

              {initError && (
                <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg mx-4 mt-2 text-xs text-amber-700">
                  <strong>Editor:</strong> {initError}{' '}
                  <button
                    onClick={() => {
                      setInitError(null);
                      hasInitialized.current = false;
                      setIsLoading(true);
                    }}
                    className="underline hover:text-amber-900"
                  >
                    Retry
                  </button>
                </div>
              )}

              <BlockNoteView
                editor={editor}
                editable={!readOnly}
                onChange={handleChange}
                onSelectionChange={handleSelectionChange}
                theme="light"
                data-placeholder={placeholder}
                formattingToolbar={false}
                slashMenu={false}
              >
                {/* Phase 6: Enhanced formatting toolbar with code button */}
                <FormattingToolbarController
                  formattingToolbar={() => (
                    <FormattingToolbar>
                      <BlockTypeSelect key="blockTypeSelect" />

                      <BasicTextStyleButton
                        basicTextStyle="bold"
                        key="boldStyleButton"
                      />
                      <BasicTextStyleButton
                        basicTextStyle="italic"
                        key="italicStyleButton"
                      />
                      <BasicTextStyleButton
                        basicTextStyle="underline"
                        key="underlineStyleButton"
                      />
                      <BasicTextStyleButton
                        basicTextStyle="strike"
                        key="strikeStyleButton"
                      />
                      <BasicTextStyleButton
                        basicTextStyle="code"
                        key="codeStyleButton"
                      />

                      <TextAlignButton
                        textAlignment="left"
                        key="textAlignLeft"
                      />
                      <TextAlignButton
                        textAlignment="center"
                        key="textAlignCenter"
                      />
                      <TextAlignButton
                        textAlignment="right"
                        key="textAlignRight"
                      />

                      <ColorStyleButton key="colorStyle" />

                      <NestBlockButton key="nestBlock" />
                      <CreateLinkButton key="createLink" />

                      {features.comments && projectId && documentId && (
                        <CommentToolbarButton
                          key="comment"
                          onClick={handleCommentClick}
                        />
                      )}
                    </FormattingToolbar>
                  )}
                />

                {/* Phase 3 & 6: Custom slash menu with /mermaid, /divider, /callout, /embed, /toggle, /columns, /synced */}
                <SuggestionMenuController
                  triggerCharacter="/"
                  getItems={getSlashMenuItems}
                />

                {/* @-mention suggestion menu — search pages and users */}
                <SuggestionMenuController
                  triggerCharacter="@"
                  getItems={getMentionMenuItems}
                />

                {/* Side menu with drag handle, copy block, and delete */}
                <SideMenuController
                  sideMenu={(props) => (
                    <SideMenu {...props}>
                      <AddBlockButton {...props} />
                      <DragHandleButton {...props} dragHandleMenu={(menuProps) => (
                        <DragHandleMenu {...menuProps}>
                          <RemoveBlockItem {...menuProps}>Delete</RemoveBlockItem>
                          <BlockColorsItem {...menuProps}>Colors</BlockColorsItem>
                          <CopyBlockItem block={menuProps.block}>Copy</CopyBlockItem>
                        </DragHandleMenu>
                      )} />
                    </SideMenu>
                  )}
                />
              </BlockNoteView>

              <style>{editorThemeCSS}</style>
            </div>

            {/* Phase 5: Floating outline on right side (toggleable with Cmd+Shift+O) */}
            {showOutline && (
              <EditorOutline
                editor={editor as unknown as BlockNoteEditorCore}
              />
            )}
          </div>

          {/* Inline comment popover */}
          {commentPopover && (
            <div className="px-6 py-3 bg-stone-50 border-t border-stone-200 animate-in slide-in-from-top-1">
              <div className="flex items-start gap-3 max-w-2xl">
                <div className="flex-1">
                  <div className="text-[11px] text-stone-400 mb-1.5 truncate">
                    Commenting on: &ldquo;{commentPopover.selectedText.slice(0, 80)}{commentPopover.selectedText.length > 80 ? '...' : ''}&rdquo;
                  </div>
                  <input
                    ref={commentInputRef}
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && commentText.trim()) submitComment();
                      if (e.key === 'Escape') { setCommentPopover(null); setCommentText(''); }
                    }}
                    placeholder="Add a comment..."
                    className="w-full text-sm px-3 py-2 rounded-lg border border-stone-200 outline-none
                             focus:border-purple-300 focus:ring-1 focus:ring-purple-200 transition-colors"
                  />
                </div>
                <div className="flex items-center gap-1.5 mt-5">
                  <button
                    onClick={submitComment}
                    disabled={!commentText.trim()}
                    className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-lg
                             hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Submit
                  </button>
                  <button
                    onClick={() => { setCommentPopover(null); setCommentText(''); }}
                    className="px-2 py-1.5 text-xs text-stone-500 hover:text-stone-700 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Comments panel (below editor, not sidebar — avoids width issues) */}
          {features.comments && projectId && documentId && (
            <div className="border-t border-stone-100">
              <button
                onClick={() => setShowComments(prev => !prev)}
                className={`flex items-center gap-1 text-xs px-6 py-1.5 transition-colors ${showComments ? 'text-purple-600' : 'text-stone-400 hover:text-stone-600'}`}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                Comments {showComments ? '(hide)' : ''}
              </button>
              {showComments && (
                <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                  <Suspense fallback={<div className="p-4 text-xs text-stone-400">Loading comments...</div>}>
                    <CommentsPanel
                      key={commentsKey}
                      projectId={projectId}
                      documentId={documentId}
                      enabled={true}
                      userName={userName}
                      userId={userId}
                    />
                  </Suspense>
                </div>
              )}
            </div>
          )}

          {/* AI Feature (Cmd+J popover) */}
          {features.ai && documentId && (
            <Suspense fallback={null}>
              <AiFeature
                projectId={projectId ?? ''}
                documentId={documentId}
                enabled={true}
                getSelection={() => {
                  const sel = editor.getSelection();
                  if (!sel) return null;
                  const text = sel.blocks.map((b: any) => {
                    if (b.content && Array.isArray(b.content)) {
                      return b.content.map((c: any) => c.text ?? '').join('');
                    }
                    return '';
                  }).join('\n');
                  return text ? { text, blockId: sel.blocks[0]?.id ?? '' } : null;
                }}
                aiApi={aiApi}
              />
            </Suspense>
          )}

          {/* Footer stats */}
          {features.stats && (
            <EditorFooter
              editor={editor as unknown as BlockNoteEditorCore}
              contentFormat={contentFormat}
            />
          )}
        </div>
      </MarthaEditorProvider>
    );
  }
);

export default MarthaEditor;
